import React, { type HTMLAttributes } from 'react';
import type { StatusColor } from '../../types/match';

type BadgeProps = HTMLAttributes<HTMLDivElement> & {
  variant?: 'solid' | 'outline';
  size?: 'xs' | 'sm' | 'md';
  color: StatusColor;
  icon?: boolean;
};

const Badge = (props: BadgeProps) => {
  const {
    children,
    variant = 'solid',
    size = 'md',
    color,
    className = '',
    icon = false,
  } = props;

  const badgeStyles = {
    solid: {
      primary: 'bg-primary-25 text-primary-500 border-2 border-primary-25',
      success: 'bg-green-100 text-green-900 border-2 border-green-100',
      warning: 'bg-yellow-100 text-yellow-900 border-2 border-yellow-100',
      error: 'bg-red-100 text-red-900 border-2 border-red-100', 
      info: 'bg-blue-100 text-blue-900 border-2 border-blue-100',
      gray: 'bg-gray-200 text-gray-700 border-2 border-gray-200',
    },
    outline: {
      primary: 'bg-primary-50 text-primary-500 border-2 border-primary-500',
      success: 'bg-green-50 text-green-500 border-2 border-green-500',
      warning: 'bg-yellow-50 text-yellow-500 border-2 border-yellow-500',
      error: 'bg-red-50 text-red-500 border-2 border-red-500',
      info: 'bg-blue-50 text-blue-500 border-2 border-blue-500',
      gray: 'bg-gray-50 text-gray-700 border-2 border-gray-200',
    },
  };

  const sizeClasses = {
    xs: 'px-4 py-1 h-6 min-w-6 w-fit text-xs leading-4 font-semibold rounded-md',
    sm: 'px-4 py-2 h-8 min-w-8 w-fit text-[0.75rem] leading-[1rem] font-medium rounded-md',
    md: 'px-4 py-3 h-10 min-w-10 w-fit text-[0.875rem] leading-[1.25rem] font-regular rounded-lg',
  };

  const iconClass = {
    xs: '!p-0 w-6 [&>svg]:w-4 [&>svg]:h-4',
    sm: '!p-0 w-8 [&>svg]:w-6 [&>svg]:h-6',
    md: '!p-0 w-10 [&>svg]:w-6 [&>svg]:h-6',
  };

  const baseClassName =
    'badge cursor-default duration-300 flex items-center justify-center font-semibold gap-2';
  const variantColorClass = badgeStyles[variant][color];
  const computedClassName = `${baseClassName} ${variantColorClass} ${sizeClasses[size]} ${className} ${icon ? iconClass[size] : ''}`;

  return <div className={computedClassName}>{children}</div>;
};

export default Badge;
